import React from "react";
import { CalendarClock } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SilaeDatePicker } from "./SilaeDatePicker";
import type { FormikProps } from "formik";
import type { SilaeFormData } from "@/types/silae";
import { useTranslation } from "react-i18next";

interface DsnPeriodicityConfigProps {
  formik: FormikProps<SilaeFormData>;
  fieldPrefix?: string;
  className?: string;
}

const BLOC52_OPTIONS = [
  {
    value: "026",
    label:
      "026 - Prime exceptionnelle liée à l'activité avec période de rattachement spécifique",
  },
  {
    value: "027",
    label:
      "027 - Prime liée à l'activité avec période de rattachement spécifique",
  },
  {
    value: "028",
    label: "028 - Prime non liée à l'activité",
  },
  {
    value: "029",
    label:
      "029 - Prime liée au repos compensateur ou au rachat des jours de RTT avec période de rattachement spécifique",
  },
  {
    value: "030",
    label: "030 - Prime rachat CET",
  },
  {
    value: "custom",
    label: "Autre - saisie manuelle",
  },
];

export const DsnPeriodicityConfig: React.FC<DsnPeriodicityConfigProps> = ({
  formik,
  fieldPrefix,
  className = "",
}) => {
  const { t } = useTranslation();

  const enableField = fieldPrefix
    ? `${fieldPrefix}.enableDsnPeriodicity`
    : "enableDsnPeriodicity";
  const dateDebutField = fieldPrefix
    ? `${fieldPrefix}.dsnDateDebut`
    : "dsnDateDebut";
  const dateFinField = fieldPrefix ? `${fieldPrefix}.dsnDateFin` : "dsnDateFin";
  const bloc52CodeField = fieldPrefix
    ? `${fieldPrefix}.bloc52Code`
    : "bloc52Code";
  const bloc52CustomCodeField = fieldPrefix
    ? `${fieldPrefix}.bloc52CustomCode`
    : "bloc52CustomCode";

  // Helper pour accéder aux valeurs imbriquées
  const getFieldValue = (path: string) => {
    const keys = path.split(".");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let value: any = formik.values;
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) return undefined;
    }
    return value;
  };

  // Helper pour accéder aux erreurs imbriquées
  const getFieldError = (path: string): string | undefined => {
    const keys = path.split(".");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let error: any = formik.errors;
    for (const key of keys) {
      error = error?.[key];
      if (error === undefined) return undefined;
    }
    return typeof error === "string" ? error : undefined;
  };

  // Helper pour accéder aux touched imbriqués
  const getFieldTouched = (path: string): boolean => {
    const keys = path.split(".");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let touched: any = formik.touched;
    for (const key of keys) {
      touched = touched?.[key];
      if (touched === undefined) return false;
    }
    return Boolean(touched);
  };

  const isEnabled = getFieldValue(enableField) as boolean;
  const bloc52Code = getFieldValue(bloc52CodeField) as string;
  const bloc52CustomCodeError = getFieldError(bloc52CustomCodeField);
  const bloc52CustomCodeTouched = getFieldTouched(bloc52CustomCodeField);
  const bloc52CodeError = getFieldError(bloc52CodeField);
  const bloc52CodeTouched = getFieldTouched(bloc52CodeField);
  const dateDebutError = getFieldError(dateDebutField);
  const dateDebutTouched = getFieldTouched(dateDebutField);
  const dateFinError = getFieldError(dateFinField);
  const dateFinTouched = getFieldTouched(dateFinField);

  return (
    <Card className={`bg-background dark:bg-background ${className}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-6 w-6 text-muted-foreground" />
            <div className="flex flex-col">
              <CardTitle className="text-lg">
                {t("dsnPeriodicityConfig.title")}
                <Badge variant="outline" className="text-xs ml-2">
                  {t("dsnPeriodicityConfig.optional")}
                </Badge>
              </CardTitle>
              <CardDescription>
                {t("dsnPeriodicityConfig.description")}
              </CardDescription>
            </div>
          </div>
          <Switch
            checked={isEnabled}
            onCheckedChange={(checked) => {
              formik.setFieldValue(enableField, checked);
              if (!checked) {
                formik.setFieldValue(dateDebutField, "Date(0,0,0)");
                formik.setFieldValue(dateFinField, "Date(0,0,0)");
                formik.setFieldValue(bloc52CodeField, "");
                formik.setFieldValue(bloc52CustomCodeField, "");
              }
              setTimeout(() => formik.validateForm(), 0);
            }}
          />
        </div>
      </CardHeader>

      {isEnabled && (
        <CardContent className="space-y-6">
          <div className="space-y-6 pt-4 border-t">
            {/* Section périodicité DSN — dates */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {t("dsnPeriodicityConfig.periodLabel")}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("dsnPeriodicityConfig.periodDescription")}
              </p>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {/* Date de début */}
              <div className="space-y-2">
                <SilaeDatePicker
                  label={t("dsnPeriodicityConfig.dateDebut")}
                  value={
                    (getFieldValue(dateDebutField) as string) || "Date(0,0,0)"
                  }
                  onChange={(value) => {
                    formik.setFieldValue(dateDebutField, value);
                    formik.setFieldTouched(dateDebutField, true, false);
                  }}
                  allowSilaeDateVariables={true}
                />
                {dateDebutTouched && dateDebutError && (
                  <p className="text-xs text-destructive">{dateDebutError}</p>
                )}
              </div>

              {/* Date de fin */}
              <div className="space-y-2">
                <SilaeDatePicker
                  label={t("dsnPeriodicityConfig.dateFin")}
                  value={
                    (getFieldValue(dateFinField) as string) || "Date(0,0,0)"
                  }
                  onChange={(value) => {
                    formik.setFieldValue(dateFinField, value);
                    formik.setFieldTouched(dateFinField, true, false);
                  }}
                  allowSilaeDateVariables={true}
                />
                {dateFinTouched && dateFinError && (
                  <p className="text-xs text-destructive">{dateFinError}</p>
                )}
              </div>
            </div>

            {/* Section Bloc 52 */}
            <div className="space-y-3 pt-2 border-t">
              <div className="space-y-1">
                <Label className="text-sm font-medium">
                  {t("dsnPeriodicityConfig.bloc52Label")}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t("dsnPeriodicityConfig.bloc52Description")}
                </p>
              </div>

              <Select
                value={bloc52Code || ""}
                onValueChange={(value) => {
                  formik.setFieldValue(bloc52CodeField, value);
                  if (value !== "custom") {
                    formik.setFieldValue(bloc52CustomCodeField, "");
                  }
                }}
              >
                <SelectTrigger
                  className={
                    (bloc52CodeTouched || formik.submitCount > 0) &&
                    bloc52CodeError
                      ? "border-destructive"
                      : ""
                  }
                >
                  <SelectValue
                    placeholder={t("dsnPeriodicityConfig.bloc52Placeholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {BLOC52_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(bloc52CodeTouched || formik.submitCount > 0) &&
                bloc52CodeError && (
                  <p className="text-xs text-destructive">{bloc52CodeError}</p>
                )}

              {/* Saisie manuelle du code bloc 52 */}
              {bloc52Code === "custom" && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t("dsnPeriodicityConfig.bloc52CustomCodeLabel")}
                  </Label>
                  <Input
                    value={
                      (getFieldValue(bloc52CustomCodeField) as string) || ""
                    }
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, "").slice(0, 3);
                      formik.setFieldValue(bloc52CustomCodeField, raw);
                    }}
                    onBlur={() =>
                      formik.setFieldTouched(bloc52CustomCodeField, true, true)
                    }
                    placeholder={t(
                      "dsnPeriodicityConfig.bloc52CustomCodePlaceholder",
                    )}
                    maxLength={3}
                    className={
                      (bloc52CustomCodeTouched || formik.submitCount > 0) &&
                      bloc52CustomCodeError
                        ? "border-destructive"
                        : ""
                    }
                  />
                  {(bloc52CustomCodeTouched || formik.submitCount > 0) &&
                    bloc52CustomCodeError && (
                      <p className="text-xs text-destructive">
                        {bloc52CustomCodeError}
                      </p>
                    )}
                </div>
              )}
            </div>

            {/* Aperçu du code généré */}
            {(getFieldValue(dateDebutField) || getFieldValue(dateFinField)) && (
              <div className="p-3 bg-slate-100 dark:bg-slate-800/50 rounded-lg border border-border space-y-1">
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  {t("dsnPeriodicityConfig.codePreview")}
                </p>
                <code className="block text-xs">
                  marquedtdeb = {getFieldValue(dateDebutField) as string}
                </code>
                <code className="block text-xs">
                  marquedtfin = {getFieldValue(dateFinField) as string}
                </code>
                {bloc52Code && bloc52Code !== "custom" && (
                  <code className="block text-xs">
                    Marque1 = &quot;b52v{bloc52Code}&quot;
                  </code>
                )}
                {bloc52Code === "custom" &&
                  getFieldValue(bloc52CustomCodeField) && (
                    <code className="block text-xs">
                      Marque1 = &quot;b52v
                      {getFieldValue(bloc52CustomCodeField) as string}&quot;
                    </code>
                  )}
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};
